import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { formatPrice } from '../utils/format';

const DEFAULTS = {
  storeName: 'Storefront',
  // No hardcoded fallbacks: empty strings hide the dependent UI
  // (Contact card phone block, footer email link, floating WhatsApp
  // pill) so unconfigured tenants don't leak a personal/legacy
  // address. Operator sets these in admin → Settings.
  storeEmail: '',
  storePhone: '',
  storeAddress: '',
  storeNipt: '',
  storeLegalName: '',
  whatsAppEnabled: false,
  whatsAppPhone: '',
  minOrderAmount: '0',
  maxItemQuantity: '99',
  lowStockThreshold: '5',
  hideOutOfStock: false,
  maintenanceMode: false,
  // Albania-first defaults, matching SettingsController::generalSettings().
  currency: 'ALL',
  currencySymbol: 'L',
  currencyPosition: 'after',
  // Returns policy is opt-in. While disabled (the default while the
  // store has no formal policy) every returns surface in the UI hides
  // itself; flipping `returnsEnabled` from the admin re-renders the
  // PDP info card, checkout trust pill, About paragraph, FAQ section
  // and the customer-side "Request return" form using `returnsWindowDays`.
  returnsEnabled: false,
  returnsWindowDays: 14,
  // Merchant defaults — set during onboarding (PHASE 2), used as
  // sensible fallbacks by the storefront until then. Albania-first
  // (post-MVP roadmap decision): defaults target the primary launch
  // market rather than generic US placeholders — still fully
  // overridable per merchant during onboarding.
  storeDefaultPhonePrefix: '+355',
  storeDefaultCountry: 'Albania',
  deliveryCountries: [],
  storeMapLat: 41.3275,
  storeMapLng: 19.8187,
  defaultLocale: 'sq',
  featureAgeRecommendations: false,
  featureDriverApp: true,
  // Onboarding (PHASE 2). storeOnboarded is `false` until the wizard is
  // completed, at which point OnboardingGate (App.js) stops redirecting.
  storeOnboarded: false,
  storeLogoUrl: '',
  timezone: 'Europe/Tirane',
  // Analytics (PHASE 3). Empty until an operator sets a real GA4 property
  // ID in Settings — GA4Bootstrap (App.js) only injects gtag.js once this
  // is non-empty.
  ga4MeasurementId: '',
  // Meta Pixel (Instagram/Facebook catalog ads) and whether analytics and
  // marketing tags wait for the visitor's consent (ConsentBanner).
  metaPixelId: '',
  marketingConsentRequired: false,
  // Storefront theming (PHASE 4). See ThemeBootstrap (App.js) and
  // frontend/src/utils/theme.js for how these map onto CSS custom
  // properties and the font-pairing registry.
  themePrimaryColor: '#2D4A3E',
  themeAccentColor: '#A94F32',
  themeFontPairing: 'classic',
  themeDesignTokens: null,
  themeGlobalSettings: { announcement: { enabled: false, text: '', url: '', openNewTab: false, style: 'static', speed: 'normal', items: [] } },
  themeHeroTitle: '',
  themeHeroSubtitle: '',
  themeHeroImage: '',
  themeShowHero: true,
  themeShowCollections: true,
  themeShowBestsellers: true,
  themeShowReviews: true,
  // PHASE 7 — additional homepage section types. Default hidden/empty
  // (unlike the four above) since there's no generic testimonial/FAQ/
  // feature copy to show until an operator writes real content.
  themeShowTestimonials: false,
  themeTestimonials: [],
  themeShowFaq: false,
  themeFaqItems: [],
  themeShowSplit: false,
  themeSplitHeading: '',
  themeSplitBody: '',
  themeSplitImage: '',
  themeSplitCtaLabel: '',
  themeSplitCtaUrl: '',
  themeShowFeatureColumns: false,
  themeFeatureColumns: [],
  // PHASE 8 — self-service footer "Quick Links". Mirrors the backend's
  // DEFAULT_FOOTER_LINKS so the footer looks identical before the first
  // settings fetch resolves and on a fresh install with nothing saved yet.
  footerLinks: [
    { label: 'Home', url: '/' },
    { label: 'Products', url: '/products' },
    { label: 'About', url: '/about' },
    { label: 'FAQ', url: '/faq' },
    { label: 'Contact', url: '/contact' },
    { label: 'Terms', url: '/terms' },
    { label: 'Privacy', url: '/privacy' },
    { label: 'Returns', url: '/returns' },
    { label: 'Shipping', url: '/shipping' },
  ],
};

const SiteSettingsContext = createContext(DEFAULTS);

export const useSiteSettings = () => useContext(SiteSettingsContext);

export const formatPriceWithCurrency = (amount, settings, lang) => {
  const symbol = settings?.currencySymbol || settings?.currency || 'USD';
  const position = settings?.currencyPosition || 'after';
  const num = formatPrice(amount || 0, { lang });
  return position === 'before' ? `${symbol} ${num}` : `${num} ${symbol}`;
};

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get('/api/settings/general')
      .then(res => {
        if (res.data) setSettings(prev => ({ ...prev, ...res.data }));
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('themePreview') === '1') return undefined;
    const previewToken = params.get('themePreviewToken');
    let active = true;
    const request = previewToken
      ? api.get(`/api/theme-previews/${previewToken}/templates/home`)
      : api.get('/api/themes/tokens');
    request.then((response) => {
      if (!active || !response.data) return;
      const presentation = previewToken ? response.data.theme : response.data;
      if (!presentation?.designTokens) return;
      setSettings((current) => ({
        ...current,
        themeDesignTokens: presentation.designTokens,
        themeGlobalSettings: presentation.globalSettings || current.themeGlobalSettings,
      }));
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  // Admin Theme page live preview (PHASE 4) — the iframe at
  // /?themePreview=1 listens for unsaved draft theme values so an operator
  // sees changes before clicking Save, with nothing persisted or fetched
  // over the network. Gated behind the query param so a normal storefront
  // visit never installs this listener, and same-origin-checked since the
  // admin page and storefront share an origin.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('themePreview') !== '1') return;
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'STOREFRONT_THEME_PREVIEW') return;
      setSettings(prev => ({ ...prev, ...event.data.payload }));
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    // currencyLabel is what shoppers read next to a price ("L"); currency
    // stays the ISO code ("ALL") for payments, analytics and structured data.
    <SiteSettingsContext.Provider value={{ ...settings, currencyLabel: settings.currencySymbol || settings.currency, loaded }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};
